import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import { useDispatch } from "react-redux";
import SaveIcon from "@mui/icons-material/Save";
import AxiosInstance from "../../services/networkServices";
import PageLayout from "../../components/PageLayout";
import LiveAvailabilityCard from "../../components/LiveAvailabilityCard";
import InProgressMatchesSection from "../InProgressMatches";
import ScheduledChanges from "./ScheduledChanges";
import SearchingPostsSection from "../InProgressMatches/SearchingPostsSection";
import {
  formatBoostMultiple,
  oldPagentBoostFactor,
} from "../InProgressMatches/matchDisplay";
import { showToast } from "../../store/slices/common";

// Ladder order, matching the backend rank constants
const RANK_ORDER = [
  "IRON",
  "BRONZE",
  "UNRANKED",
  "SILVER",
  "GOLD",
  "PLATINUM",
  "DIAMOND",
  "LEGEND",
  "GRANDLEGEND",
  "IDOLIZED",
];

const RANK_LABELS = {
  UNRANKED: "Unranked",
  IRON: "Iron",
  BRONZE: "Bronze",
  SILVER: "Silver",
  GOLD: "Gold",
  PLATINUM: "Platinum",
  DIAMOND: "Diamond",
  LEGEND: "Legend",
  GRANDLEGEND: "Grand Legend",
  IDOLIZED: "Idolized",
  STAFF: "Staff",
};

// Categories whose feed weights can be overridden, matching the backend's
// FEED_WEIGHT_CATEGORIES short names.
const FEED_WEIGHT_CATEGORIES = [
  { key: "karaoke", label: "Karaoke" },
  { key: "dance", label: "Dance" },
  { key: "fashion", label: "Fashion" },
  { key: "makeup", label: "Makeup" },
  { key: "art", label: "Art" },
  { key: "pets", label: "Pets" },
  { key: "rap", label: "Rap" },
];

// Categories whose "first to X votes" tables can be overridden, matching the
// backend's VOTE_THRESHOLD_CATEGORIES (feed weight categories minus rap: rap
// battles are live matches that end on the battle timer, never on votes).
const VOTE_THRESHOLD_CATEGORIES = FEED_WEIGHT_CATEGORIES.filter(
  ({ key }) => key !== "rap"
);

// Vote requirement modes for the timed pagent auto-end, matching the
// backend's TIMED_MATCH_END_VOTE_CRITERIA.
const TIMED_END_CRITERIA_OPTIONS = [
  { value: "UNIQUE_VOTERS", label: "Unique voters" },
  { value: "TOTAL_VOTES", label: "Total votes (weighted)" },
  { value: "BOTH", label: "Both" },
];

// Pre-vote hourglass timer sections, matching the backend's
// VOTING_UNLOCK_SECTIONS (votingUnlock.constants.js). The mobile VS feed
// keys its unlock timers by these sections; group pagents use their base
// section's value.
const VOTING_UNLOCK_SECTIONS = [
  {
    key: "challenges",
    label: "Challenges",
    hint: "Photo/video prompt pagents (fashion, makeup, pets, ...)",
  },
  { key: "dance", label: "Dance" },
  { key: "karaoke", label: "Karaoke" },
  { key: "rap", label: "Rap" },
  { key: "art", label: "Art" },
];
const VOTING_UNLOCK_MAX_SECONDS = 30;

/** Ages shown under Old Pagent Feed Boost. steps = floor(age / 15 min). */
const OLD_PAGENT_BOOST_AGES = [
  { label: "Under 15 min", steps: 0 },
  { label: "15 min", steps: 1 },
  { label: "30 min", steps: 2 },
  { label: "45 min", steps: 3 },
  { label: "1 hour", steps: 4 },
  { label: "2 hours", steps: 8 },
  { label: "6 hours", steps: 24 },
  { label: "1 day", steps: 96 },
];

/** Convert a per-rank number map into a per-rank string map for text inputs. */
const toInputMap = (map) =>
  Object.fromEntries(RANK_ORDER.map((rank) => [rank, String(map?.[rank] ?? "")]));

/** 1234 -> "1,234"; missing/non-numeric -> "0". */
const formatCount = (value) => (Number(value) || 0).toLocaleString();

/** Share of total as a whole-number percent; em dash when total is 0. */
const formatShare = (users, total) =>
  total > 0 ? `${Math.round(((Number(users) || 0) / total) * 100)}%` : "\u2014";

/**
 * Human-readable pairing retry schedule from base minutes + max backoff
 * steps, e.g. "2 → 4 → 8 → 16 → 32 min (then stays at 32)". Returns null
 * when the inputs aren't valid yet so the helper text can stay quiet.
 */
const formatRetrySchedule = (baseMinutesRaw, maxExponentRaw) => {
  const baseMinutes = Number(baseMinutesRaw);
  const maxExponent = Number(maxExponentRaw);
  if (
    !Number.isInteger(baseMinutes) ||
    baseMinutes < 1 ||
    !Number.isInteger(maxExponent) ||
    maxExponent < 0
  ) {
    return null;
  }
  const steps = [];
  for (let exp = 0; exp <= maxExponent; exp += 1) {
    steps.push(baseMinutes * Math.pow(2, exp));
  }
  const ceiling = steps[steps.length - 1];
  if (steps.length === 1) {
    return `${ceiling} min every retry (fixed interval)`;
  }
  return `${steps.join(" \u2192 ")} min (then stays at ${ceiling})`;
};

/**
 * How long after a save to refetch the active-match list a second time. The
 * backend restamps thresholds before responding but ends newly-qualifying
 * matches in a background sweep, so one more refresh a few seconds later
 * picks up the matches that sweep closed.
 */
const POST_SAVE_REFRESH_DELAY_MS = 6000;

/**
 * Describe what a save did to in-progress matches and energy bars, from
 * the backend's `applied` block. Empty string when nothing about
 * thresholds, the timed end, or the energy bar size changed.
 */
const describeApplied = (applied) => {
  if (!applied) return "";
  const parts = [];

  const thresholds = applied.voteThresholds || {};
  const changedRanks = Object.keys(thresholds.changed || {});
  const changedCategories = Object.keys(thresholds.categoryChanged || {});
  if (changedRanks.length > 0 || changedCategories.length > 0) {
    const total = thresholds.restampedTotal || 0;
    const scopes = [];
    if (changedRanks.length > 0) {
      scopes.push(changedRanks.map((rank) => RANK_LABELS[rank] || rank).join(", "));
    }
    for (const key of changedCategories) {
      const label =
        VOTE_THRESHOLD_CATEGORIES.find((c) => c.key === key)?.label || key;
      const ranks = Object.keys(thresholds.categoryChanged[key] || {})
        .map((rank) => RANK_LABELS[rank] || rank)
        .join(", ");
      scopes.push(`${label} ${ranks}`);
    }
    parts.push(
      `${total} in-progress ${total === 1 ? "match" : "matches"} moved to the new ${scopes.join(
        "; "
      )} vote ${
        changedRanks.length + changedCategories.length === 1 ? "threshold" : "thresholds"
      }` + (thresholds.sweepStarted ? "; matches already at or over it are ending now" : "")
    );
  }

  const timed = applied.timedMatchEnd || {};
  if (timed.sweepStarted) {
    parts.push("timed pagent end is being applied to matches that already qualify");
  } else if (timed.changed) {
    parts.push("timed pagent end updated");
  }

  const energy = applied.energy;
  if (energy?.previousMax != null && energy?.nextMax != null) {
    parts.push(
      `energy bars kept the same fill percentage (${energy.previousMax} → ${energy.nextMax})`
    );
  }

  if (!parts.length) return "";
  const sentences = parts.map((part) => part.replace(/^./, (c) => c.toUpperCase()));
  return ` ${sentences.join(". ")}.`;
};

/** Disabled threshold override for every category (tables filled on load). */
const emptyCategoryThresholdOverrides = () =>
  Object.fromEntries(
    VOTE_THRESHOLD_CATEGORIES.map(({ key }) => [
      key,
      { enabled: false, thresholds: toInputMap({}) },
    ])
  );

/** Disabled override state for every category (tables filled on load). */
const emptyCategoryOverrides = () =>
  Object.fromEntries(
    FEED_WEIGHT_CATEGORIES.map(({ key }) => [
      key,
      {
        enabled: false,
        preEnergy: toInputMap({}),
        fullEnergy: toInputMap({}),
        consolidated: toInputMap({}),
      },
    ])
  );

const headCellSx = {
  fontWeight: 700,
  color: "#142C5C",
  backgroundColor: "#f4f6fb",
  fontSize: "13px",
};

const hexToRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** Pastel card with a solid accent so neighboring settings stay distinct. */
const cardSx = (accent) => ({
  padding: "20px",
  borderRadius: "10px",
  boxShadow: "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
  height: "100%",
  boxSizing: "border-box",
  backgroundColor: hexToRgba(accent, 0.1),
  borderTop: `5px solid ${accent}`,
});

/** Wide enough for multi-digit weights, thresholds, and radii. */
const numberFieldSx = {
  width: "100%",
  minWidth: "240px",
  maxWidth: "420px",
};

const SectionHeading = ({ title, detail }) => (
  <Box sx={{ pt: 0.5 }}>
    <Typography variant="h5" sx={{ fontWeight: 800, color: "#142C5C", lineHeight: 1.2 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
      {detail}
    </Typography>
  </Box>
);

const GameplaySettings = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [energyMax, setEnergyMax] = useState("");
  const [groupPagentFeedWeight, setGroupPagentFeedWeight] = useState("");
  const [liveMatchFeedWeight, setLiveMatchFeedWeight] = useState("");
  const [oldMatchFeedBoost, setOldMatchFeedBoost] = useState("");
  const [timedEndEnabled, setTimedEndEnabled] = useState(false);
  const [timedEndMinAgeHours, setTimedEndMinAgeHours] = useState("");
  const [timedEndCriteria, setTimedEndCriteria] = useState("UNIQUE_VOTERS");
  const [timedEndMinUniqueVoters, setTimedEndMinUniqueVoters] = useState("");
  const [timedEndMinTotalVotes, setTimedEndMinTotalVotes] = useState("");
  const [pastFeedEnabled, setPastFeedEnabled] = useState(false);
  const [pastFeedPastCount, setPastFeedPastCount] = useState("");
  const [pastFeedActiveCount, setPastFeedActiveCount] = useState("");
  const [pastFeedMinRank, setPastFeedMinRank] = useState("GOLD");
  const [liveStreamMinRank, setLiveStreamMinRank] = useState("GOLD");
  const [consolidatedEnabled, setConsolidatedEnabled] = useState(false);
  const [consolidatedShowPast, setConsolidatedShowPast] = useState(false);
  // Geo matchmaking (IP-based approximate location): pairing search radii
  // and feed serving radii per rank, their widening schedules, the queued
  // pairing retry wait (base × 2^min(attempt, maxExponent)), and the
  // profile-region fallback. Both kill switches ship off. geoAvailable
  // tracks whether the backend sent the block at all — against an older
  // backend the cards are hidden and the payload omits it, so deploy order
  // between admin and backend never blocks saving.
  // Minimum matchmaking wait before a post can be paired (backend stores
  // seconds; the input works in whole minutes). Availability-guarded like
  // geo so older backends neither render nor receive the field.
  const [matchmakingDelayAvailable, setMatchmakingDelayAvailable] = useState(false);
  const [matchmakingDelayMinutes, setMatchmakingDelayMinutes] = useState("");
  // Live ride-along: viewers of a Home/Discover livestream boosting the
  // host's energy and sending IAP gifts. Availability-guarded like geo so an
  // older backend neither renders the card nor receives the field.
  const [rideAlongAvailable, setRideAlongAvailable] = useState(false);
  const [rideAlongBoostEnabled, setRideAlongBoostEnabled] = useState(true);
  const [rideAlongBoostCooldownSeconds, setRideAlongBoostCooldownSeconds] = useState("");
  const [rideAlongBoostEnergy, setRideAlongBoostEnergy] = useState("");
  const [rideAlongGiftsEnabled, setRideAlongGiftsEnabled] = useState(true);
  const [rideAlongGiftEnergyBonusEnabled, setRideAlongGiftEnergyBonusEnabled] =
    useState(true);
  const [rideAlongCreatorSharePercent, setRideAlongCreatorSharePercent] = useState("");
  const [geoAvailable, setGeoAvailable] = useState(false);
  const [geoPairingEnabled, setGeoPairingEnabled] = useState(false);
  const [geoFeedEnabled, setGeoFeedEnabled] = useState(false);
  const [geoRegionFallbackEnabled, setGeoRegionFallbackEnabled] = useState(true);
  const [geoPairingBaseRadiusKm, setGeoPairingBaseRadiusKm] = useState(
    toInputMap({})
  );
  const [geoFeedBaseRadiusKm, setGeoFeedBaseRadiusKm] = useState(toInputMap({}));
  const [geoPairingExpansionFactor, setGeoPairingExpansionFactor] = useState("");
  const [geoPairingGlobalAttempt, setGeoPairingGlobalAttempt] = useState("");
  // Retry wait: backend stores base seconds; the input is whole minutes
  // (same pattern as Matchmaking Wait). Max backoff exponent stays an
  // integer step count (0 = fixed interval, no doubling).
  const [geoPairingRetryBaseMinutes, setGeoPairingRetryBaseMinutes] = useState("");
  const [geoPairingRetryMaxBackoffExponent, setGeoPairingRetryMaxBackoffExponent] =
    useState("");
  const [geoFeedGrowthKmPerHour, setGeoFeedGrowthKmPerHour] = useState("");
  const [geoGlobalRadiusKm, setGeoGlobalRadiusKm] = useState("");
  const [votingUnlockSeconds, setVotingUnlockSeconds] = useState(
    Object.fromEntries(VOTING_UNLOCK_SECTIONS.map(({ key }) => [key, ""]))
  );
  const [voteThresholds, setVoteThresholds] = useState(toInputMap({}));
  const [forFunVoteThreshold, setForFunVoteThreshold] = useState("20");
  const [forFunFeedWeights, setForFunFeedWeights] = useState({
    preEnergy: "1",
    fullEnergy: "1",
    consolidated: "1",
  });
  // Per-category "first to X" tables (e.g. fashion needing more votes to
  // end). Availability-guarded like geo: against an older backend the card
  // is hidden and the payload omits the field.
  const [categoryThresholdsAvailable, setCategoryThresholdsAvailable] =
    useState(false);
  const [categoryThresholdOverrides, setCategoryThresholdOverrides] = useState(
    emptyCategoryThresholdOverrides()
  );
  const [fullEnergyRankWeights, setFullEnergyRankWeights] = useState(
    toInputMap({})
  );
  const [preEnergyRankWeights, setPreEnergyRankWeights] = useState(
    toInputMap({})
  );
  const [consolidatedRankWeights, setConsolidatedRankWeights] = useState(
    toInputMap({})
  );
  const [categoryOverrides, setCategoryOverrides] = useState(
    emptyCategoryOverrides()
  );
  // Flat per-category feed multipliers (e.g. fashion 2 = fashion pagents
  // surface about twice as often), on top of the rank weights for every
  // viewer. Availability-guarded like geo: against an older backend the card
  // is hidden and the payload omits the field.
  const [categoryFeedWeightsAvailable, setCategoryFeedWeightsAvailable] =
    useState(false);
  const [categoryFeedWeights, setCategoryFeedWeights] = useState(
    Object.fromEntries(FEED_WEIGHT_CATEGORIES.map(({ key }) => [key, "1"]))
  );
  // Read-only counts of users who selected each category in their
  // preferences; loaded with the settings, not part of the save payload.
  const [categoryPreferences, setCategoryPreferences] = useState(null);
  // Read-only counts of non-deleted users at each voting power level (1-5).
  const [votingPowerCounts, setVotingPowerCounts] = useState(null);
  const [rankDistribution, setRankDistribution] = useState(null);
  // Voting-power and rank counts are read-only and slower than the settings
  // document. They fill in on their own so they don't hold the page spinner.
  const [countsLoading, setCountsLoading] = useState(true);
  // Bumped after a save so the Active Matches section below refetches and
  // shows the restamped thresholds / ended matches.
  const [matchesRefreshToken, setMatchesRefreshToken] = useState(0);
  const postSaveRefreshTimer = useRef(null);
  // Last settings loaded or saved. Scheduling restores threshold and weight
  // fields from this so the form does not keep the future values.
  const liveGameplayRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    return () => clearTimeout(postSaveRefreshTimer.current);
  }, []);

  const applySchedulableFields = (
    gameplay,
    { thresholds = true, weights = true } = {}
  ) => {
    if (!thresholds && !weights) return;

    if (thresholds) {
      setVoteThresholds(toInputMap(gameplay.voteThresholds));
      setForFunVoteThreshold(String(gameplay.forFunVoteThreshold ?? 20));

      // Saved category threshold tables arrive enabled; the rest start
      // disabled but prefilled from the base table, so toggling one on begins
      // from the values it would otherwise inherit.
      const savedThresholds = gameplay.categoryVoteThresholds;
      setCategoryThresholdsAvailable(savedThresholds !== undefined);
      setCategoryThresholdOverrides(
        Object.fromEntries(
          VOTE_THRESHOLD_CATEGORIES.map(({ key }) => [
            key,
            {
              enabled: Boolean(savedThresholds?.[key]),
              thresholds: toInputMap(
                savedThresholds?.[key] ?? gameplay.voteThresholds
              ),
            },
          ])
        )
      );
    }

    if (weights) {
      const savedForFunWeights = gameplay.forFunFeedWeights || {};
      setForFunFeedWeights({
        preEnergy: String(savedForFunWeights.preEnergy ?? 1),
        fullEnergy: String(savedForFunWeights.fullEnergy ?? 1),
        consolidated: String(savedForFunWeights.consolidated ?? 1),
      });
      setFullEnergyRankWeights(toInputMap(gameplay.fullEnergyRankWeights));
      setPreEnergyRankWeights(toInputMap(gameplay.preEnergyRankWeights));
      setConsolidatedRankWeights(toInputMap(gameplay.consolidatedRankWeights));

      // Saved overrides arrive enabled with their own tables; the rest start
      // disabled but prefilled from the current defaults, so toggling one on
      // begins from the values it would otherwise inherit.
      const saved = gameplay.categoryRankWeights || {};
      setCategoryOverrides(
        Object.fromEntries(
          FEED_WEIGHT_CATEGORIES.map(({ key }) => {
            const override = saved[key];
            return [
              key,
              {
                enabled: Boolean(override),
                preEnergy: toInputMap(
                  override?.preEnergy ?? gameplay.preEnergyRankWeights
                ),
                fullEnergy: toInputMap(
                  override?.fullEnergy ?? gameplay.fullEnergyRankWeights
                ),
                consolidated: toInputMap(
                  override?.consolidated ?? gameplay.consolidatedRankWeights
                ),
              },
            ];
          })
        )
      );
    }
  };

  const applyGameplay = (gameplay) => {
    liveGameplayRef.current = gameplay;
    setEnergyMax(String(gameplay.energyMax ?? ""));
    setGroupPagentFeedWeight(String(gameplay.groupDuetFeedWeight ?? ""));
    setLiveMatchFeedWeight(String(gameplay.liveMatchFeedWeight ?? ""));
    setOldMatchFeedBoost(String(gameplay.oldMatchFeedBoost ?? ""));
    const timedEnd = gameplay.timedMatchEnd || {};
    setTimedEndEnabled(Boolean(timedEnd.enabled));
    setTimedEndMinAgeHours(String(timedEnd.minAgeHours ?? ""));
    setTimedEndCriteria(timedEnd.voteCriteria || "UNIQUE_VOTERS");
    setTimedEndMinUniqueVoters(String(timedEnd.minUniqueVoters ?? ""));
    setTimedEndMinTotalVotes(String(timedEnd.minTotalVotes ?? ""));
    const pastFeed = gameplay.pastMatchFeedFrequency || {};
    setPastFeedEnabled(Boolean(pastFeed.enabled));
    setPastFeedPastCount(String(pastFeed.pastCount ?? ""));
    setPastFeedActiveCount(String(pastFeed.activeCount ?? ""));
    setPastFeedMinRank(pastFeed.minRankTier || "GOLD");
    setLiveStreamMinRank(
      RANK_ORDER.includes(gameplay.liveStreamMinRankTier)
        ? gameplay.liveStreamMinRankTier
        : "GOLD"
    );
    const consolidated = gameplay.consolidatedFeed || {};
    setConsolidatedEnabled(Boolean(consolidated.enabled));
    setConsolidatedShowPast(Boolean(consolidated.showPastPagents));
    const delaySeconds = gameplay.matchmakingDelaySeconds;
    setMatchmakingDelayAvailable(delaySeconds !== undefined);
    setMatchmakingDelayMinutes(
      delaySeconds !== undefined ? String(Math.round(delaySeconds / 60)) : ""
    );
    const rideAlong = gameplay.liveRideAlong;
    setRideAlongAvailable(Boolean(rideAlong));
    setRideAlongBoostEnabled(rideAlong?.boostEnabled !== false);
    setRideAlongBoostCooldownSeconds(String(rideAlong?.boostCooldownSeconds ?? ""));
    setRideAlongBoostEnergy(String(rideAlong?.boostEnergy ?? ""));
    setRideAlongGiftsEnabled(rideAlong?.giftsEnabled !== false);
    setRideAlongGiftEnergyBonusEnabled(rideAlong?.giftEnergyBonusEnabled !== false);
    setRideAlongCreatorSharePercent(String(rideAlong?.creatorSharePercent ?? ""));
    const geo = gameplay.geoMatchmaking || {};
    setGeoAvailable(Boolean(gameplay.geoMatchmaking));
    setGeoPairingEnabled(Boolean(geo.pairingEnabled));
    setGeoFeedEnabled(Boolean(geo.feedEnabled));
    setGeoRegionFallbackEnabled(geo.regionFallbackEnabled !== false);
    setGeoPairingBaseRadiusKm(toInputMap(geo.pairingBaseRadiusKm));
    setGeoFeedBaseRadiusKm(toInputMap(geo.feedBaseRadiusKm));
    setGeoPairingExpansionFactor(String(geo.pairingExpansionFactor ?? ""));
    setGeoPairingGlobalAttempt(String(geo.pairingGlobalAttempt ?? ""));
    // Older backends may omit the retry fields; fall back to the known
    // deploy-time defaults (2 min base, 4 backoff steps → 32 min ceiling)
    // so the card stays editable and saves don't fail mid-deploy.
    const retryBaseSeconds = geo.pairingRetryBaseSeconds;
    setGeoPairingRetryBaseMinutes(
      retryBaseSeconds !== undefined
        ? String(Math.round(retryBaseSeconds / 60))
        : "2"
    );
    setGeoPairingRetryMaxBackoffExponent(
      String(geo.pairingRetryMaxBackoffExponent ?? "4")
    );
    setGeoFeedGrowthKmPerHour(String(geo.feedGrowthKmPerHour ?? ""));
    setGeoGlobalRadiusKm(String(geo.globalRadiusKm ?? ""));
    const unlockSeconds = gameplay.votingUnlockSeconds || {};
    setVotingUnlockSeconds(
      Object.fromEntries(
        VOTING_UNLOCK_SECTIONS.map(({ key }) => [
          key,
          String(unlockSeconds[key] ?? ""),
        ])
      )
    );
    applySchedulableFields(gameplay);

    // Flat category multipliers: the backend only stores categories that
    // were saved, so anything missing is shown at its effective value of 1.
    const savedFeedWeights = gameplay.categoryFeedWeights;
    setCategoryFeedWeightsAvailable(savedFeedWeights !== undefined);
    setCategoryFeedWeights(
      Object.fromEntries(
        FEED_WEIGHT_CATEGORIES.map(({ key }) => [
          key,
          String(savedFeedWeights?.[key] ?? 1),
        ])
      )
    );
  };

  const fetchSettings = async () => {
    setLoading(true);
    setCountsLoading(true);
    const votingPowerPromise = AxiosInstance.get("/admin/voting-power-levels").catch(
      (error) => {
        console.error("Error fetching voting power counts:", error);
        return null;
      }
    );
    const rankDistributionPromise = AxiosInstance.get("/admin/rank-distribution").catch(
      (error) => {
        console.error("Error fetching rank distribution:", error);
        return null;
      }
    );

    try {
      const response = await AxiosInstance.get("/admin/settings/gameplay");
      if (response.data.status) {
        applyGameplay(response.data.data.gameplay);
        setCategoryPreferences(response.data.data.categoryPreferences || null);
      } else {
        dispatch(
          showToast({ color: "error", msg: "Failed to load gameplay settings." })
        );
      }
    } catch (error) {
      console.error("Error fetching gameplay settings:", error);
      dispatch(
        showToast({ color: "error", msg: "Failed to load gameplay settings." })
      );
    } finally {
      setLoading(false);
    }

    try {
      const [votingPowerResult, rankDistributionResult] = await Promise.all([
        votingPowerPromise,
        rankDistributionPromise,
      ]);
      setVotingPowerCounts(
        votingPowerResult?.data?.status ? votingPowerResult.data.data : null
      );
      setRankDistribution(
        rankDistributionResult?.data?.status ? rankDistributionResult.data.data : null
      );
    } finally {
      setCountsLoading(false);
    }
  };

  /**
   * Vote thresholds and rank weights only. Same rules as a full save, so a
   * schedule can be built without validating energy, geo, or the other cards.
   * Returns the typed fields, or null after showing the first problem.
   */
  const readSchedulableFields = () => {
    const fail = (msg) => {
      dispatch(showToast({ color: "error", msg }));
      return null;
    };

    const thresholds = {};
    for (const rank of RANK_ORDER) {
      const value = Number(voteThresholds[rank]);
      if (!Number.isInteger(value) || value < 1) {
        return fail(
          `${RANK_LABELS[rank]} vote threshold must be a whole number of at least 1.`
        );
      }
      thresholds[rank] = value;
    }

    const forFunThreshold = Number(forFunVoteThreshold);
    if (!Number.isInteger(forFunThreshold) || forFunThreshold < 1) {
      return fail("For fun vote threshold must be a whole number of at least 1.");
    }

    const readForFunWeight = (raw, label) => {
      const value = Number(raw);
      if (String(raw ?? "").trim() === "" || !Number.isFinite(value) || value <= 0) {
        return fail(`For fun ${label} weight must be a number greater than 0.`);
      }
      return value;
    };
    const forFunPre = readForFunWeight(forFunFeedWeights.preEnergy, "filling-bar");
    if (forFunPre === null) return null;
    const forFunFull = readForFunWeight(forFunFeedWeights.fullEnergy, "full-bar");
    if (forFunFull === null) return null;
    const forFunConsolidated = readForFunWeight(
      forFunFeedWeights.consolidated,
      "consolidated"
    );
    if (forFunConsolidated === null) return null;

    // Per-category "first to X" tables: every rank of every enabled
    // category must be a whole number >= 1. Only validated and sent when
    // the backend supports the field.
    let categoryVoteThresholds = null;
    if (categoryThresholdsAvailable) {
      categoryVoteThresholds = {};
      for (const { key, label } of VOTE_THRESHOLD_CATEGORIES) {
        const override = categoryThresholdOverrides[key];
        if (!override?.enabled) continue;
        const table = {};
        for (const rank of RANK_ORDER) {
          const value = Number(override.thresholds[rank]);
          if (!Number.isInteger(value) || value < 1) {
            return fail(
              `${label} ${RANK_LABELS[rank]} vote threshold must be a whole number of at least 1.`
            );
          }
          table[rank] = value;
        }
        categoryVoteThresholds[key] = table;
      }
    }

    const readRankWeights = (values, describe) => {
      const weights = {};
      for (const rank of RANK_ORDER) {
        const value = Number(values[rank]);
        if (!Number.isFinite(value) || value <= 0) {
          return fail(
            `${describe} ${RANK_LABELS[rank]} weight must be a number greater than 0.`
          );
        }
        weights[rank] = value;
      }
      return weights;
    };

    const fullWeights = readRankWeights(fullEnergyRankWeights, "Full-bar");
    if (!fullWeights) return null;

    const preWeights = readRankWeights(preEnergyRankWeights, "Filling-bar");
    if (!preWeights) return null;

    const consolidatedWeights = readRankWeights(
      consolidatedRankWeights,
      "Consolidated"
    );
    if (!consolidatedWeights) return null;

    const categoryRankWeights = {};
    for (const { key, label } of FEED_WEIGHT_CATEGORIES) {
      const override = categoryOverrides[key];
      if (!override?.enabled) continue;

      const pre = readRankWeights(override.preEnergy, `${label} filling-bar`);
      if (!pre) return null;
      const full = readRankWeights(override.fullEnergy, `${label} full-bar`);
      if (!full) return null;
      const consolidatedOverride = readRankWeights(
        override.consolidated,
        `${label} consolidated`
      );
      if (!consolidatedOverride) return null;

      categoryRankWeights[key] = {
        preEnergy: pre,
        fullEnergy: full,
        consolidated: consolidatedOverride,
      };
    }

    return {
      voteThresholds: thresholds,
      forFunVoteThreshold: forFunThreshold,
      forFunFeedWeights: {
        preEnergy: forFunPre,
        fullEnergy: forFunFull,
        consolidated: forFunConsolidated,
      },
      fullEnergyRankWeights: fullWeights,
      preEnergyRankWeights: preWeights,
      consolidatedRankWeights: consolidatedWeights,
      categoryRankWeights,
      ...(categoryVoteThresholds && { categoryVoteThresholds }),
    };
  };

  /**
   * Mirror the backend validation: energy max is an integer 1-100, vote
   * thresholds are integers >= 1, weights are numbers > 0 (including every
   * rank of every enabled category override). Returns the typed payload, or
   * null after showing the first problem found.
   */
  const buildPayload = () => {
    const fail = (msg) => {
      dispatch(showToast({ color: "error", msg }));
      return null;
    };

    const energy = Number(energyMax);
    if (!Number.isInteger(energy) || energy < 1 || energy > 100) {
      return fail("Energy bar size must be a whole number between 1 and 100.");
    }

    const schedulable = readSchedulableFields();
    if (!schedulable) return null;

    // Flat category multipliers: every category must hold a number > 0
    // (1 = no change). Only validated and sent when the backend supports it.
    let categoryFeedWeightsPayload = null;
    if (categoryFeedWeightsAvailable) {
      categoryFeedWeightsPayload = {};
      for (const { key, label } of FEED_WEIGHT_CATEGORIES) {
        const value = Number(categoryFeedWeights[key]);
        if (
          String(categoryFeedWeights[key] ?? "").trim() === "" ||
          !Number.isFinite(value) ||
          value <= 0
        ) {
          return fail(
            `${label} feed weight must be a number greater than 0 (1 = no change).`
          );
        }
        categoryFeedWeightsPayload[key] = value;
      }
    }

    // Past pagent feed: counts and minimum rank are prefilled from the server
    // so they must always parse, enabled or not (they round-trip like the
    // weight tables).
    const readPastCount = (value, labelText) => {
      const num = Number(value);
      if (!Number.isInteger(num) || num < 1) {
        return fail(`${labelText} must be a whole number of at least 1.`);
      }
      return num;
    };
    const pastCount = readPastCount(
      pastFeedPastCount,
      "Past pagents shown per group"
    );
    if (pastCount === null) return null;
    const activeCount = readPastCount(
      pastFeedActiveCount,
      "Active pagents per group"
    );
    if (activeCount === null) return null;
    if (!RANK_ORDER.includes(pastFeedMinRank)) {
      return fail("Minimum past pagent rank must be one of the rank tiers.");
    }
    if (!RANK_ORDER.includes(liveStreamMinRank)) {
      return fail("Minimum Go Live rank must be one of the rank tiers.");
    }

    const pastMatchFeedFrequency = {
      enabled: pastFeedEnabled,
      pastCount,
      activeCount,
      minRankTier: pastFeedMinRank,
    };

    const consolidatedFeed = {
      enabled: consolidatedEnabled,
      showPastPagents: consolidatedShowPast,
    };

    // Pre-vote hourglass timers: 0.5-second steps between 0 and the max —
    // the app ships one baked hourglass animation per step, so off-step
    // values would have no exact-duration animation. 0 = no timer.
    const votingUnlock = {};
    for (const { key, label } of VOTING_UNLOCK_SECTIONS) {
      const raw = String(votingUnlockSeconds[key] ?? "").trim();
      const value = Number(raw);
      if (
        raw === "" ||
        !Number.isFinite(value) ||
        value < 0 ||
        value > VOTING_UNLOCK_MAX_SECONDS ||
        !Number.isInteger(value * 2)
      ) {
        return fail(
          `${label} hourglass must be 0 to ${VOTING_UNLOCK_MAX_SECONDS} seconds in half-second steps (0 = no timer).`
        );
      }
      votingUnlock[key] = value;
    }

    const groupWeight = Number(groupPagentFeedWeight);
    if (!Number.isFinite(groupWeight) || groupWeight <= 0) {
      return fail("Group pagent feed weight must be a number greater than 0.");
    }

    const liveWeight = Number(liveMatchFeedWeight);
    if (!Number.isFinite(liveWeight) || liveWeight <= 0) {
      return fail("Live match feed weight must be a number greater than 0.");
    }

    const oldBoost = Number(oldMatchFeedBoost);
    if (!Number.isFinite(oldBoost) || oldBoost <= 0) {
      return fail("Old pagent feed boost rate must be a number greater than 0.");
    }

    // Timed pagent end: the fields the selected criteria uses are required
    // only while the rule is enabled. The minimum not used by the criteria
    // is still sent when valid so it round-trips; otherwise the backend
    // keeps its default for that field.
    const timedEndUsesUnique =
      timedEndCriteria === "UNIQUE_VOTERS" || timedEndCriteria === "BOTH";
    const timedEndUsesTotal =
      timedEndCriteria === "TOTAL_VOTES" || timedEndCriteria === "BOTH";

    const timedEndHours = Number(timedEndMinAgeHours);
    const timedEndHoursValid =
      Number.isFinite(timedEndHours) && timedEndHours > 0;
    const readVoteMinimum = (value) => {
      const raw = String(value ?? "").trim();
      if (raw === "") return null;
      const num = Number(raw);
      // 0 is allowed: a pagent with no votes still ends once it is old enough.
      return Number.isInteger(num) && num >= 0 ? num : null;
    };
    const timedEndUnique = readVoteMinimum(timedEndMinUniqueVoters);
    const timedEndTotal = readVoteMinimum(timedEndMinTotalVotes);

    if (timedEndEnabled) {
      if (!timedEndHoursValid) {
        return fail(
          "Timed pagent end age must be a number of hours greater than 0."
        );
      }
      if (timedEndUsesUnique && timedEndUnique === null) {
        return fail(
          "Timed pagent end minimum unique voters must be a whole number of 0 or more."
        );
      }
      if (timedEndUsesTotal && timedEndTotal === null) {
        return fail(
          "Timed pagent end minimum total votes must be a whole number of 0 or more."
        );
      }
    }

    const timedMatchEnd = {
      enabled: timedEndEnabled,
      voteCriteria: timedEndCriteria,
      ...(timedEndHoursValid && { minAgeHours: timedEndHours }),
      ...(timedEndUnique !== null && { minUniqueVoters: timedEndUnique }),
      ...(timedEndTotal !== null && { minTotalVotes: timedEndTotal }),
    };

    // Matchmaking wait: whole minutes, 0 (pair immediately) to 24 hours.
    // Only validated and sent when the backend supports the field.
    let matchmakingDelaySeconds = null;
    if (matchmakingDelayAvailable) {
      const delayMinutes = Number(matchmakingDelayMinutes);
      if (!Number.isInteger(delayMinutes) || delayMinutes < 0 || delayMinutes > 1440) {
        return fail(
          "Matchmaking wait must be a whole number of minutes between 0 and 1440 (0 = pair immediately)."
        );
      }
      matchmakingDelaySeconds = delayMinutes * 60;
    }

    // Live ride-along: only validated and sent when the backend supports it.
    // Backend bounds: cooldown 5–3600s, boost 1–100 energy, creator share
    // 0–70% (stores keep ~15–30% of list price, so the share must stay
    // below the net payout).
    let liveRideAlong = null;
    if (rideAlongAvailable) {
      const cooldown = Number(rideAlongBoostCooldownSeconds);
      if (!Number.isInteger(cooldown) || cooldown < 5 || cooldown > 3600) {
        return fail(
          "Boost cooldown must be a whole number of seconds between 5 and 3600."
        );
      }
      const boostEnergy = Number(rideAlongBoostEnergy);
      if (!Number.isInteger(boostEnergy) || boostEnergy < 1 || boostEnergy > 100) {
        return fail("Energy per boost must be a whole number between 1 and 100.");
      }
      const share = Number(rideAlongCreatorSharePercent);
      if (!Number.isInteger(share) || share < 0 || share > 70) {
        return fail(
          "Creator share must be a whole percentage between 0 and 70 (must stay below the store's net payout)."
        );
      }
      liveRideAlong = {
        boostEnabled: rideAlongBoostEnabled,
        boostCooldownSeconds: cooldown,
        boostEnergy,
        giftsEnabled: rideAlongGiftsEnabled,
        giftEnergyBonusEnabled: rideAlongGiftEnergyBonusEnabled,
        creatorSharePercent: share,
      };
    }

    // Geo matchmaking: only validated and sent when the backend supports it
    // (geoAvailable — the GET returned the block, with every field merged
    // over defaults, so the inputs are prefilled and must always parse).
    // Backend rules: radii > 0 per rank, growth factor >= 1, global attempt
    // is a whole number >= 1, feed growth >= 0 (0 freezes the radius),
    // global radius > 0. Values at or above the global radius mean
    // "worldwide".
    let geoMatchmaking = null;
    if (geoAvailable) {
      const readRadiusTable = (values, describe) => {
        const table = {};
        for (const rank of RANK_ORDER) {
          const value = Number(values[rank]);
          if (!Number.isFinite(value) || value <= 0) {
            return fail(
              `${describe} ${RANK_LABELS[rank]} radius must be a number of km greater than 0.`
            );
          }
          table[rank] = value;
        }
        return table;
      };
      const geoPairingRadii = readRadiusTable(
        geoPairingBaseRadiusKm,
        "Pairing search"
      );
      if (!geoPairingRadii) return null;
      const geoFeedRadii = readRadiusTable(geoFeedBaseRadiusKm, "Feed serving");
      if (!geoFeedRadii) return null;

      const geoExpansion = Number(geoPairingExpansionFactor);
      if (!Number.isFinite(geoExpansion) || geoExpansion < 1) {
        return fail(
          "Pairing radius growth factor must be a number of at least 1 (1 = no widening)."
        );
      }
      const geoGlobalAttempt = Number(geoPairingGlobalAttempt);
      if (!Number.isInteger(geoGlobalAttempt) || geoGlobalAttempt < 1) {
        return fail(
          "Pairing worldwide-at-attempt must be a whole number of at least 1."
        );
      }
      // Retry base: whole minutes → seconds. Backend accepts 30–3600s
      // (0.5–60 min); whole-minute UI covers 1–60.
      const retryBaseMinutes = Number(geoPairingRetryBaseMinutes);
      if (
        !Number.isInteger(retryBaseMinutes) ||
        retryBaseMinutes < 1 ||
        retryBaseMinutes > 60
      ) {
        return fail(
          "Time between pairing attempts must be a whole number of minutes between 1 and 60."
        );
      }
      const retryMaxExponent = Number(geoPairingRetryMaxBackoffExponent);
      if (!Number.isInteger(retryMaxExponent) || retryMaxExponent < 0 || retryMaxExponent > 10) {
        return fail(
          "Pairing retry max backoff steps must be a whole number between 0 and 10 (0 = fixed interval)."
        );
      }
      const geoGrowth = Number(geoFeedGrowthKmPerHour);
      if (!Number.isFinite(geoGrowth) || geoGrowth < 0) {
        return fail(
          "Feed radius growth must be a number of km per hour of at least 0 (0 = radius never widens)."
        );
      }
      const geoGlobal = Number(geoGlobalRadiusKm);
      if (!Number.isFinite(geoGlobal) || geoGlobal <= 0) {
        return fail("Worldwide radius must be a number of km greater than 0.");
      }

      geoMatchmaking = {
        pairingEnabled: geoPairingEnabled,
        feedEnabled: geoFeedEnabled,
        regionFallbackEnabled: geoRegionFallbackEnabled,
        pairingBaseRadiusKm: geoPairingRadii,
        pairingExpansionFactor: geoExpansion,
        pairingGlobalAttempt: geoGlobalAttempt,
        pairingRetryBaseSeconds: retryBaseMinutes * 60,
        pairingRetryMaxBackoffExponent: retryMaxExponent,
        feedBaseRadiusKm: geoFeedRadii,
        feedGrowthKmPerHour: geoGrowth,
        globalRadiusKm: geoGlobal,
      };
    }

    return {
      energyMax: energy,
      ...schedulable,
      groupDuetFeedWeight: groupWeight,
      liveMatchFeedWeight: liveWeight,
      oldMatchFeedBoost: oldBoost,
      timedMatchEnd,
      pastMatchFeedFrequency,
      consolidatedFeed,
      votingUnlockSeconds: votingUnlock,
      liveStreamMinRankTier: liveStreamMinRank,
      // Omitted (not cleared) when the backend predates these fields
      ...(categoryFeedWeightsPayload && {
        categoryFeedWeights: categoryFeedWeightsPayload,
      }),
      ...(matchmakingDelaySeconds !== null && { matchmakingDelaySeconds }),
      ...(liveRideAlong && { liveRideAlong }),
      ...(geoMatchmaking && { geoMatchmaking }),
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) return;

    try {
      setSaving(true);
      const response = await AxiosInstance.put(
        "/admin/settings/gameplay",
        payload
      );
      if (response.data.status) {
        if (response.data.data?.gameplay) {
          applyGameplay(response.data.data.gameplay);
        }
        const applied = response.data.data?.applied;
        dispatch(
          showToast({
            color: "success",
            msg: `Gameplay settings saved.${describeApplied(applied)}`,
          })
        );
        // Refresh the active-match list now (restamped thresholds are
        // already in place) and once more after the background sweeps
        // have had a moment to end the matches that now qualify.
        setMatchesRefreshToken((token) => token + 1);
        clearTimeout(postSaveRefreshTimer.current);
        if (
          applied?.voteThresholds?.sweepStarted ||
          applied?.timedMatchEnd?.sweepStarted
        ) {
          postSaveRefreshTimer.current = setTimeout(
            () => setMatchesRefreshToken((token) => token + 1),
            POST_SAVE_REFRESH_DELAY_MS
          );
        }
      } else {
        dispatch(
          showToast({
            color: "error",
            msg: response.data.message || "Failed to save gameplay settings.",
          })
        );
      }
    } catch (error) {
      console.error("Error saving gameplay settings:", error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error?.response?.data?.message ||
            "Failed to save gameplay settings.",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  /**
   * Snapshot the threshold and/or weight fields currently in the form and
   * arm them for `scheduledAt`. Live settings are not written. On success
   * the form fields return to the last loaded values.
   */
  const handleScheduleChange = async ({
    scheduledAt,
    includeThresholds,
    includeWeights,
  }) => {
    const fields = readSchedulableFields();
    if (!fields) return false;

    const changes = {};
    if (includeThresholds) {
      changes.voteThresholds = fields.voteThresholds;
      changes.forFunVoteThreshold = fields.forFunVoteThreshold;
      if (fields.categoryVoteThresholds) {
        changes.categoryVoteThresholds = fields.categoryVoteThresholds;
      }
    }
    if (includeWeights) {
      changes.fullEnergyRankWeights = fields.fullEnergyRankWeights;
      changes.preEnergyRankWeights = fields.preEnergyRankWeights;
      changes.consolidatedRankWeights = fields.consolidatedRankWeights;
      changes.categoryRankWeights = fields.categoryRankWeights;
      changes.forFunFeedWeights = fields.forFunFeedWeights;
    }

    try {
      const response = await AxiosInstance.post("/admin/settings/gameplay/scheduled", {
        scheduledAt,
        ...changes,
      });
      if (!response.data.status) {
        dispatch(
          showToast({
            color: "error",
            msg: response.data.message || "Failed to schedule that change.",
          })
        );
        return false;
      }
      if (liveGameplayRef.current) {
        applySchedulableFields(liveGameplayRef.current, {
          thresholds: includeThresholds,
          weights: includeWeights,
        });
      }
      const groups = [
        includeThresholds ? "vote thresholds" : null,
        includeWeights ? "rank weights" : null,
      ]
        .filter(Boolean)
        .join(" and ");
      dispatch(
        showToast({
          color: "success",
          msg: `Scheduled the ${groups}. Those fields are back to the live values.`,
        })
      );
      return true;
    } catch (error) {
      console.error("Error scheduling gameplay change:", error);
      dispatch(
        showToast({
          color: "error",
          msg: error?.response?.data?.message || "Failed to schedule that change.",
        })
      );
      return false;
    }
  };

  const toggleCategoryThresholdOverride = (key, enabled) =>
    setCategoryThresholdOverrides((prev) => ({
      ...prev,
      [key]: { ...prev[key], enabled },
    }));

  const setCategoryThreshold = (key, rank, value) =>
    setCategoryThresholdOverrides((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        thresholds: { ...prev[key].thresholds, [rank]: value },
      },
    }));

  const toggleCategoryOverride = (key, enabled) =>
    setCategoryOverrides((prev) => ({
      ...prev,
      [key]: { ...prev[key], enabled },
    }));

  const setCategoryWeight = (key, state, rank, value) =>
    setCategoryOverrides((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [state]: { ...prev[key][state], [rank]: value },
      },
    }));

  const renderRankTable = (values, onChange, columnLabel, inputStep) => (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>Rank</TableCell>
            <TableCell sx={headCellSx} align="right">
              {columnLabel}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {RANK_ORDER.map((rank) => (
            <TableRow key={rank}>
              <TableCell sx={{ fontWeight: 600 }}>{RANK_LABELS[rank]}</TableCell>
              <TableCell align="right" sx={{ width: "200px", minWidth: "200px" }}>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0, step: inputStep, style: { textAlign: "right" } }}
                  value={values[rank]}
                  onChange={(e) => onChange(rank, e.target.value)}
                  fullWidth
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  /**
   * One input column per weight table (filling bar / full bar / consolidated)
   * per rank. Columns: [{ label, values, onChange }].
   * extraRow, when set, is a non-rank row (For fun) aligned to those columns.
   */
  const renderRankWeightColumns = (columns, extraRow = null) => (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>Rank</TableCell>
            {columns.map(({ label }) => (
              <TableCell key={label} sx={headCellSx} align="right">
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {RANK_ORDER.map((rank) => (
            <TableRow key={rank}>
              <TableCell sx={{ fontWeight: 600 }}>{RANK_LABELS[rank]}</TableCell>
              {columns.map(({ label, values, onChange }) => (
                <TableCell key={label} align="right" sx={{ width: "180px", minWidth: "180px" }}>
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: 0.5, style: { textAlign: "right" } }}
                    value={values[rank]}
                    onChange={(e) => onChange(rank, e.target.value)}
                    fullWidth
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
          {extraRow && (
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>{extraRow.label}</TableCell>
              {extraRow.cells.map(({ key, value, onChange }) => (
                <TableCell key={key} align="right" sx={{ width: "180px", minWidth: "180px" }}>
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{
                      min: 0,
                      step: 0.5,
                      style: { textAlign: "right" },
                      "aria-label": `${extraRow.label} ${key} weight`,
                    }}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    fullWidth
                  />
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <PageLayout
      title="Match & Energy"
      subtitle="Live gameplay tuning plus the active match list and the matchmaking pool (posts searching for a pagent). Energy bar size, how the player base is split across public ranks, how many users are at each voting power level (1–5), feed weights per rank for filling-bar, full-bar, and consolidated feeds, per-category weight overrides, how many users selected each category in their preferences, group pagent, live match, old pagent, and per-category feed boosts, the past pagent feed, feed consolidation, the matchmaking wait, pairing geo (radii, retry wait), voting-feed geo, per-rank vote thresholds with per-category overrides, the timed pagent auto-end, and the pre-vote hourglass timers. Changes take effect within about 30 seconds, no deploy needed; vote threshold and timed pagent end changes are applied to in-progress matches the moment you save. Vote thresholds and rank weights can instead be scheduled for a specific date and time; thresholds restamp in-progress matches when that time arrives."
      loading={loading}
      actions={
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={loading || saving}
          sx={{ textTransform: "none" }}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12} sx={{ order: 0 }}>
          <SectionHeading
            title="Players"
            detail="Who is playing, how their votes count, and the energy bar."
          />
        </Grid>
        <Grid item xs={12} sx={{ order: 7 }}>
          <SectionHeading
            title="Voting feed"
            detail="Consolidation sits with the rank weights and the extra boosts that stack on top of them."
          />
        </Grid>
        <Grid item xs={12} sx={{ order: 19 }}>
          <SectionHeading
            title="How a pagent ends"
            detail="Vote targets, category overrides, the timed auto-end, and scheduled threshold or rank-weight changes."
          />
        </Grid>
        <Grid item xs={12} sx={{ order: 29 }}>
          <SectionHeading
            title="Matchmaking"
            detail="How long a post waits to be paired, and where opponents are searched."
          />
        </Grid>

        <Grid item xs={12} sx={{ order: 1 }}>
          <Paper sx={cardSx("#3949AB")}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Voting Power
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              How many users are at each voting power level. A vote from that
              user counts as the level&apos;s weight. Everyone starts at level
              1. Read-only; refreshed when this page loads.
            </Typography>
            {countsLoading && !votingPowerCounts ? (
              <Typography variant="body2" color="text.secondary">
                Loading voting power counts…
              </Typography>
            ) : votingPowerCounts?.levels?.length ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(3, minmax(0, 1fr))",
                    md: "repeat(5, minmax(0, 1fr))",
                  },
                  gap: "12px",
                }}
              >
                {votingPowerCounts.levels.map((row) => (
                  <Box
                    key={row.level}
                    sx={{
                      border: "1px solid rgba(224, 224, 224, 1)",
                      borderRadius: "8px",
                      padding: "12px",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "13px", fontWeight: 700, color: "#142C5C" }}
                    >
                      Level {row.level}
                    </Typography>
                    <Typography sx={{ fontSize: "22px", fontWeight: 700, lineHeight: 1.3 }}>
                      {formatCount(row.count)}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
                      {formatShare(row.count, votingPowerCounts.total)} of users
                      · weight ×{row.weight}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Voting power counts are unavailable.
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} sx={{ order: 2 }}>
          <Paper sx={cardSx("#00897B")}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Rank distribution
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Share of the player base at each public rank
              {rankDistribution?.total != null
                ? ` (${formatCount(rankDistribution.total)} players)`
                : ""}
              . Only active player accounts are counted. Players with no rank
              record count as Unranked. Read-only; refreshed when this page
              loads.
            </Typography>
            {countsLoading && !rankDistribution ? (
              <Typography variant="body2" color="text.secondary">
                Loading rank distribution…
              </Typography>
            ) : rankDistribution?.ranks?.length ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(3, minmax(0, 1fr))",
                    md: "repeat(5, minmax(0, 1fr))",
                  },
                  gap: "12px",
                }}
              >
                {rankDistribution.ranks.map((row) => (
                  <Box
                    key={row.tier}
                    sx={{
                      border: "1px solid rgba(224, 224, 224, 1)",
                      borderRadius: "8px",
                      padding: "12px",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "13px", fontWeight: 700, color: "#142C5C" }}
                    >
                      {RANK_LABELS[row.tier] || row.tier}
                    </Typography>
                    <Typography sx={{ fontSize: "22px", fontWeight: 700, lineHeight: 1.3 }}>
                      {formatShare(row.count, rankDistribution.total)}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
                      {formatCount(row.count)} players
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Rank distribution is unavailable.
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: 3 }}>
          <Paper sx={cardSx("#F9A825")}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Energy Bar
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Votes required to fill the energy bar. A full bar is required to
              submit a post (and is consumed by it). Changing the size keeps
              each player's fill percentage: a full bar stays full, and a
              half-full bar stays half full.
            </Typography>
            <TextField
              label="Energy bar size (votes to fill)"
              type="number"
              size="small"
              inputProps={{ min: 1, max: 100, step: 1 }}
              value={energyMax}
              onChange={(e) => setEnergyMax(e.target.value)}
              sx={numberFieldSx}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: 12 }}>
          <Paper sx={cardSx("#2E7D32")}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Group Pagent Feed Weight
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Multiplier applied to group pagent matches in the voting feed,
              on top of the rank weights. 1 means no boost over solo matches;
              2 makes them surface about twice as often. Decimals are allowed.
            </Typography>
            <TextField
              label="Group pagent feed weight"
              type="number"
              size="small"
              inputProps={{ min: 0, step: 0.5 }}
              value={groupPagentFeedWeight}
              onChange={(e) => setGroupPagentFeedWeight(e.target.value)}
              sx={numberFieldSx}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: 13 }}>
          <Paper sx={cardSx("#C62828")}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Live Match Feed Weight
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Unused. Live battles now use the same Rank Weights tables
              (and Category Weight Overrides / Category Feed Weights) as
              regular matches for the viewer&apos;s energy state. This value
              is kept so saves stay compatible with older clients.
            </Typography>
            <TextField
              label="Live match feed weight (unused)"
              type="number"
              size="small"
              inputProps={{ min: 0, step: 0.5 }}
              value={liveMatchFeedWeight}
              onChange={(e) => setLiveMatchFeedWeight(e.target.value)}
              sx={numberFieldSx}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: 14 }}>
          <Paper sx={cardSx("#EF6C00")}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Old Pagent Feed Boost
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Each in-progress pagent is multiplied by this rate once every 15
              minutes it stays open, on top of the rank weights, until it ends.
              1 means no boost. Decimals are allowed. The table updates as you
              type.
            </Typography>
            <TextField
              label="Boost per 15 minutes"
              type="number"
              size="small"
              inputProps={{ min: 0, step: 0.01 }}
              value={oldMatchFeedBoost}
              onChange={(e) => setOldMatchFeedBoost(e.target.value)}
              sx={numberFieldSx}
            />
            <Table size="small" sx={{ mt: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Age</TableCell>
                  <TableCell sx={headCellSx}>Feed multiplier</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {OLD_PAGENT_BOOST_AGES.map(({ label, steps }) => {
                  const multiple = oldPagentBoostFactor(oldMatchFeedBoost, steps * 15 * 60 * 1000);
                  return (
                    <TableRow key={label}>
                      <TableCell sx={{ fontSize: "13px" }}>{label}</TableCell>
                      <TableCell sx={{ fontSize: "13px", fontWeight: 700 }}>
                        {multiple == null ? "—" : formatBoostMultiple(multiple)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {categoryFeedWeightsAvailable && (
          <Grid item xs={12} md={6} sx={{ order: 11 }}>
            <Paper sx={cardSx("#00838F")}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Category Feed Weights
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Multiplier applied to every pagent in a category in the voting
                feed, on top of the rank weights and the group / old pagent
                boosts. 1 means no change; 2 makes that category surface about
                twice as often, 0.5 about half as often. Applies to all voters
                whether their energy bar is full or not, and stacks with any
                Category Weight Override below. Live battles (rap, karaoke)
                use their category value to scale interleave weight instead
                of the pipeline score. Contest pagents are unaffected.
                Decimals are allowed.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {FEED_WEIGHT_CATEGORIES.map(({ key, label }) => (
                  <TextField
                    key={key}
                    label={`${label} feed weight`}
                    type="number"
                    size="small"
                    inputProps={{ min: 0, step: 0.5 }}
                    value={categoryFeedWeights[key]}
                    onChange={(e) =>
                      setCategoryFeedWeights((prev) => ({
                        ...prev,
                        [key]: e.target.value,
                      }))
                    }
                    sx={numberFieldSx}
                  />
                ))}
              </Box>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={6} sx={{ order: 17 }}>
          <Paper sx={cardSx("#455A64")}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Pre-Vote Hourglass Timers
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Seconds the sandglass runs before voting unlocks on a pagent,
              per category. Half-second steps up to 30 &mdash; the app ships
              one hourglass animation per step, with the sand running out
              right as voting unlocks. 0 disables the timer (voting unlocks
              immediately). Group pagents use their base category&apos;s
              timer. Running apps pick changes up on their next launch or
              feed refresh.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {VOTING_UNLOCK_SECTIONS.map(({ key, label, hint }) => (
                <TextField
                  key={key}
                  label={`${label} (seconds)`}
                  type="number"
                  size="small"
                  inputProps={{ min: 0, max: VOTING_UNLOCK_MAX_SECONDS, step: 0.5 }}
                  value={votingUnlockSeconds[key]}
                  onChange={(e) =>
                    setVotingUnlockSeconds((prev) => ({
                      ...prev,
                      [key]: e.target.value,
                    }))
                  }
                  helperText={hint}
                  sx={numberFieldSx}
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: 21 }}>
          <Paper sx={cardSx("#880E4F")}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Timed Pagent End
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={timedEndEnabled}
                    onChange={(e) => setTimedEndEnabled(e.target.checked)}
                  />
                }
                label={timedEndEnabled ? "On" : "Off"}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ends pagents that have been running longer than the age below
              once they meet the vote requirement, without waiting for the
              first-to-X target. The side leading at that moment wins; a tie
              is a draw. Set a minimum to 0 to also end pagents nobody has
              voted on — those close as a draw. Unique voters counts people;
              total votes adds each vote&apos;s voting-power weight (1&ndash;5),
              so higher-level voters count for more. Checked every 15 minutes,
              and run immediately when you save a change here &mdash; any
              in-progress pagent that already meets the new rule ends right
              away.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Minimum pagent age (hours)"
                type="number"
                size="small"
                inputProps={{ min: 0, step: 1 }}
                value={timedEndMinAgeHours}
                onChange={(e) => setTimedEndMinAgeHours(e.target.value)}
                disabled={!timedEndEnabled}
                sx={numberFieldSx}
              />
              <TextField
                label="Vote requirement"
                select
                size="small"
                value={timedEndCriteria}
                onChange={(e) => setTimedEndCriteria(e.target.value)}
                disabled={!timedEndEnabled}
                sx={numberFieldSx}
              >
                {TIMED_END_CRITERIA_OPTIONS.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              {(timedEndCriteria === "UNIQUE_VOTERS" ||
                timedEndCriteria === "BOTH") && (
                <TextField
                  label="Minimum unique voters"
                  type="number"
                  size="small"
                  inputProps={{ min: 0, step: 1 }}
                  value={timedEndMinUniqueVoters}
                  onChange={(e) => setTimedEndMinUniqueVoters(e.target.value)}
                  disabled={!timedEndEnabled}
                  sx={numberFieldSx}
                />
              )}
              {(timedEndCriteria === "TOTAL_VOTES" ||
                timedEndCriteria === "BOTH") && (
                <TextField
                  label="Minimum total votes (weighted)"
                  type="number"
                  size="small"
                  inputProps={{ min: 0, step: 1 }}
                  value={timedEndMinTotalVotes}
                  onChange={(e) => setTimedEndMinTotalVotes(e.target.value)}
                  disabled={!timedEndEnabled}
                  sx={numberFieldSx}
                />
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: 9 }}>
          <Paper sx={cardSx("#C2185B")}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Past Pagent Feed
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={pastFeedEnabled}
                    onChange={(e) => setPastFeedEnabled(e.target.checked)}
                  />
                }
                label={pastFeedEnabled ? "On" : "Off"}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Weaves past pagents into every viewer&apos;s in-progress voting
              feed — filling bar and full bar alike, guests included — at the
              rhythm below. 1 per 3 shows one past pagent after every three
              active ones. Karaoke and dance come in runs of 3 of the same
              song, including when some of the three are past pagents sitting
              in that in-progress feed. A run is never split by this weave,
              and a woven past karaoke or dance pagent brings two more past
              pagents of its song with it. Woven past pagents are completed
              pagents of the current week&apos;s challenges, karaoke songs, and
              dances, taken from the highest rank down, at or above the
              minimum rank, filtered by the viewer&apos;s category preferences.
              Lower tiers and other weeks are not filled in when that pool
              runs short, and a pagent the viewer has already voted on is not
              shown again.
              While Feed Consolidation is on, its own &quot;show past
              pagents&quot; switch replaces this toggle (the rhythm and
              minimum rank here still apply).
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Show X past pagents"
                type="number"
                size="small"
                inputProps={{ min: 1, step: 1 }}
                value={pastFeedPastCount}
                onChange={(e) => setPastFeedPastCount(e.target.value)}
                disabled={!pastFeedEnabled && !consolidatedEnabled}
                sx={numberFieldSx}
              />
              <TextField
                label="Per Y active pagents"
                type="number"
                size="small"
                inputProps={{ min: 1, step: 1 }}
                value={pastFeedActiveCount}
                onChange={(e) => setPastFeedActiveCount(e.target.value)}
                disabled={!pastFeedEnabled && !consolidatedEnabled}
                sx={numberFieldSx}
              />
              <TextField
                label="Minimum past pagent rank"
                select
                size="small"
                value={pastFeedMinRank}
                onChange={(e) => setPastFeedMinRank(e.target.value)}
                disabled={!pastFeedEnabled && !consolidatedEnabled}
                sx={numberFieldSx}
              >
                {RANK_ORDER.map((rank) => (
                  <MenuItem key={rank} value={rank}>
                    {RANK_LABELS[rank]}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: 8 }}>
          <Paper sx={cardSx("#6A1B9A")}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Feed Consolidation
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={consolidatedEnabled}
                    onChange={(e) => setConsolidatedEnabled(e.target.checked)}
                  />
                }
                label={consolidatedEnabled ? "On" : "Off"}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Collapses the filling-bar / full-bar feed split into one feed
              for every viewer, guests included. While on, the feed uses the
              &quot;Consolidated&quot; weight column below instead of the two
              energy-state columns, and the switch here — not the energy bar —
              decides whether past pagents are woven into the in-progress
              voting feed (at the rhythm and minimum rank set on the Past
              Pagent Feed card). Karaoke and dance runs of 3 of the same song
              stay together there even when some of the three are past pagents.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={consolidatedShowPast}
                  onChange={(e) => setConsolidatedShowPast(e.target.checked)}
                  disabled={!consolidatedEnabled}
                />
              }
              label="Show past pagents in the consolidated feed"
            />
          </Paper>
        </Grid>

        {matchmakingDelayAvailable && (
          <Grid item xs={12} md={6} sx={{ order: 30 }}>
            <Paper sx={cardSx("#283593")}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Matchmaking Wait
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Minimum time a post sits out before its first pairing
                attempt (and again after each pagent ends before a rematch).
                A longer wait builds a deeper pool so matchmaking can honor
                both the skill (ELO) band and, when Pairing Geo is on, the
                location radius, instead of instantly pairing whoever happens
                to be free. This is not the wait between failed retries —
                that lives on the Pairing Geo card. 0 pairs immediately.
                Contest pagents are unaffected (random pairing).
              </Typography>
              <TextField
                label="Matchmaking wait (minutes)"
                type="number"
                size="small"
                inputProps={{ min: 0, max: 1440, step: 5 }}
                value={matchmakingDelayMinutes}
                onChange={(e) => setMatchmakingDelayMinutes(e.target.value)}
                sx={numberFieldSx}
              />
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={6} sx={{ order: 29 }}>
          <Paper sx={cardSx("#00695C")}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Go Live from Home
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Minimum rank required to start a livestream from the home feed.
              Players below it see the rank-required message when they tap
              Go Live. Staff can always go live. Direct posts stay Gold.
            </Typography>
            <TextField
              label="Minimum rank to go live"
              select
              size="small"
              value={liveStreamMinRank}
              onChange={(e) => setLiveStreamMinRank(e.target.value)}
              sx={numberFieldSx}
            >
              {RANK_ORDER.map((rank) => (
                <MenuItem key={rank} value={rank}>
                  {RANK_LABELS[rank]}
                </MenuItem>
              ))}
            </TextField>
          </Paper>
        </Grid>

        <Grid item xs={12} sx={{ order: 29.5 }}>
          <LiveAvailabilityCard
            title="Go Live availability"
            featureLabel="Go Live"
            endpoint="/admin/settings/go-live-availability"
            responseKey="goLiveAvailability"
            showNotificationsToggle={false}
            showMasterToggle
            alwaysOnWithoutWindows
            statusVerb="is"
            scheduleHelp="The home feed shows Go Live only while this is on. With the switch on and no windows, it stays available all week. Add windows to open it only during those times. Starting a stream is also rejected while it is off."
          />
        </Grid>

        {rideAlongAvailable && (
          <Grid item xs={12} md={6} sx={{ order: 30 }}>
            <Paper sx={cardSx("#AD1457")}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Live Ride-Along
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Viewers watching a livestream from Home or Discover spectate
                the host&apos;s live pagents (they never vote in them) and can
                keep the host going: a free Boost adds energy once per
                cooldown, and gifts are store purchases whose creator share
                is credited to the host&apos;s cash balance. The host sees
                boosts and gifts on stream in real time.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={rideAlongBoostEnabled}
                    onChange={(e) => setRideAlongBoostEnabled(e.target.checked)}
                  />
                }
                label={rideAlongBoostEnabled ? "Boosts on" : "Boosts off"}
                sx={{ display: "block", mb: 1 }}
              />
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <TextField
                  label="Boost cooldown (seconds)"
                  type="number"
                  size="small"
                  inputProps={{ min: 5, max: 3600, step: 5 }}
                  value={rideAlongBoostCooldownSeconds}
                  onChange={(e) => setRideAlongBoostCooldownSeconds(e.target.value)}
                  disabled={!rideAlongBoostEnabled}
                  sx={numberFieldSx}
                />
                <TextField
                  label="Energy per boost"
                  type="number"
                  size="small"
                  inputProps={{ min: 1, max: 100, step: 1 }}
                  value={rideAlongBoostEnergy}
                  onChange={(e) => setRideAlongBoostEnergy(e.target.value)}
                  disabled={!rideAlongBoostEnabled}
                  sx={numberFieldSx}
                />
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={rideAlongGiftsEnabled}
                    onChange={(e) => setRideAlongGiftsEnabled(e.target.checked)}
                  />
                }
                label={rideAlongGiftsEnabled ? "Gifts on" : "Gifts off"}
                sx={{ display: "block" }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={rideAlongGiftEnergyBonusEnabled}
                    onChange={(e) =>
                      setRideAlongGiftEnergyBonusEnabled(e.target.checked)
                    }
                    disabled={!rideAlongGiftsEnabled}
                  />
                }
                label="Gifts also add their tier's energy bonus to the host"
                sx={{ display: "block", mb: 1 }}
              />
              <TextField
                label="Creator share of gift price (%)"
                type="number"
                size="small"
                inputProps={{ min: 0, max: 70, step: 1 }}
                value={rideAlongCreatorSharePercent}
                onChange={(e) => setRideAlongCreatorSharePercent(e.target.value)}
                disabled={!rideAlongGiftsEnabled}
                sx={numberFieldSx}
                helperText="Percent of the store list price credited to the host. Keep below the store's net payout (~70%)."
              />
            </Paper>
          </Grid>
        )}

        {geoAvailable && (
          <>
            <Grid item xs={12} md={6} sx={{ order: 31 }}>
              <Paper sx={cardSx("#00695C")}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 0.5,
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Pairing Geo
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={geoPairingEnabled}
                        onChange={(e) => setGeoPairingEnabled(e.target.checked)}
                      />
                    }
                    label={geoPairingEnabled ? "On" : "Off"}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  When on, queued opponent search (prompt/KOH, karaoke, dance,
                  art) only considers posts inside the seeker&apos;s
                  rank-based radius. Location is city-level from IP — never
                  GPS. Radius at attempt 0 comes from the table below; each
                  failed retry multiplies it by the growth factor, then snaps
                  to worldwide at the attempt below (also when the tier gap
                  relaxes). Group duets, contests, and live/chess/debate are
                  not geo-gated yet. An unlocated seeker is not gated. Radii
                  at or above the worldwide radius mean no limit. Run the geo
                  backfill script before turning this on.
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={geoRegionFallbackEnabled}
                      onChange={(e) =>
                        setGeoRegionFallbackEnabled(e.target.checked)
                      }
                    />
                  }
                  label="Fall back to the user's profile region when their IP can't be located (pairing and feed)"
                  sx={{ mb: 1.5, display: "block" }}
                />
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 1, color: "#142C5C" }}
                >
                  Search radius schedule
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                  <TextField
                    label="Radius growth factor (per retry)"
                    type="number"
                    size="small"
                    inputProps={{ min: 1, step: 0.1 }}
                    value={geoPairingExpansionFactor}
                    onChange={(e) => setGeoPairingExpansionFactor(e.target.value)}
                    helperText="1 = never widen; 1.5 = ×1.5 each retry"
                    sx={numberFieldSx}
                  />
                  <TextField
                    label="Worldwide at attempt #"
                    type="number"
                    size="small"
                    inputProps={{ min: 1, step: 1 }}
                    value={geoPairingGlobalAttempt}
                    onChange={(e) => setGeoPairingGlobalAttempt(e.target.value)}
                    helperText="Zero-based retry count that drops the geo gate"
                    sx={numberFieldSx}
                  />
                  <TextField
                    label="Worldwide radius (km)"
                    type="number"
                    size="small"
                    inputProps={{ min: 1, step: 1 }}
                    value={geoGlobalRadiusKm}
                    onChange={(e) => setGeoGlobalRadiusKm(e.target.value)}
                    helperText="Shared sentinel for pairing and feed (≥ this = no limit)"
                    sx={numberFieldSx}
                  />
                </Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 1, color: "#142C5C" }}
                >
                  Time between pairing attempts
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  After a miss, the post waits before the next search. Wait =
                  base × 2^min(attempt, max steps), then stays at the ceiling
                  so long searches keep a steady cadence. Shorter waits retry
                  faster (and widen the geo/ELO nets sooner); longer waits
                  ease queue load. Separate from Matchmaking Wait above
                  (pool depth before the first try).
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                  <TextField
                    label="Base wait (minutes)"
                    type="number"
                    size="small"
                    inputProps={{ min: 1, max: 60, step: 1 }}
                    value={geoPairingRetryBaseMinutes}
                    onChange={(e) =>
                      setGeoPairingRetryBaseMinutes(e.target.value)
                    }
                    helperText="Attempt 0 wait; doubles each retry until the ceiling"
                    sx={numberFieldSx}
                  />
                  <TextField
                    label="Max backoff steps"
                    type="number"
                    size="small"
                    inputProps={{ min: 0, max: 10, step: 1 }}
                    value={geoPairingRetryMaxBackoffExponent}
                    onChange={(e) =>
                      setGeoPairingRetryMaxBackoffExponent(e.target.value)
                    }
                    helperText={
                      formatRetrySchedule(
                        geoPairingRetryBaseMinutes,
                        geoPairingRetryMaxBackoffExponent
                      ) || "0 = fixed interval (no doubling)"
                    }
                    sx={numberFieldSx}
                  />
                </Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 1, color: "#142C5C" }}
                >
                  Base search radius by rank (km at attempt 0)
                </Typography>
                {renderRankTable(
                  geoPairingBaseRadiusKm,
                  (rank, value) =>
                    setGeoPairingBaseRadiusKm((prev) => ({
                      ...prev,
                      [rank]: value,
                    })),
                  "Search radius (km)",
                  1
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6} sx={{ order: 16 }}>
              <Paper sx={cardSx("#558B2F")}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 0.5,
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Voting Feed Geo
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={geoFeedEnabled}
                        onChange={(e) => setGeoFeedEnabled(e.target.checked)}
                      />
                    }
                    label={geoFeedEnabled ? "On" : "Off"}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  When on, the VS voting feed only serves in-progress pagents
                  whose posters are within the voter&apos;s reach: effective
                  radius = rank base + (pagent age in hours × km/hour growth),
                  capped at the worldwide radius. Feed bases are wider than
                  pairing on purpose — a pagent needs a bigger voter
                  catchment than the area its posters came from. Contests are
                  always exempt. An unlocatable voter sees the ungated feed.
                  If a gated page is short, it tops up from the worldwide
                  pool so thin regions never starve. Profile-region fallback
                  and the worldwide radius are shared with Pairing Geo. Run
                  the geo backfill script before turning this on.
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 1, color: "#142C5C" }}
                >
                  Serve radius schedule
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                  <TextField
                    label="Radius growth (km per hour of age)"
                    type="number"
                    size="small"
                    inputProps={{ min: 0, step: 10 }}
                    value={geoFeedGrowthKmPerHour}
                    onChange={(e) => setGeoFeedGrowthKmPerHour(e.target.value)}
                    helperText="0 = radius never widens with age"
                    sx={numberFieldSx}
                  />
                </Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 1, color: "#142C5C" }}
                >
                  Base serve radius by rank (km at age 0)
                </Typography>
                {renderRankTable(
                  geoFeedBaseRadiusKm,
                  (rank, value) =>
                    setGeoFeedBaseRadiusKm((prev) => ({
                      ...prev,
                      [rank]: value,
                    })),
                  "Serve radius (km)",
                  1
                )}
              </Paper>
            </Grid>
          </>
        )}

        <Grid item xs={12} md={6} sx={{ order: 20 }}>
          <Paper sx={cardSx("#D84315")}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Vote Thresholds
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A match ends as soon as one side reaches its rank&apos;s
              &quot;first to X votes&quot; target. Saving applies a changed
              value to every in-progress match of that rank immediately: their
              &quot;First to X&quot; moves to the new number, up or down, and
              any match already at or over a lowered target ends right away.
            </Typography>
            {renderRankTable(
              voteThresholds,
              (rank, value) =>
                setVoteThresholds((prev) => ({ ...prev, [rank]: value })),
              "First to X votes",
              1
            )}
            <TextField
              label="For fun vote threshold"
              type="number"
              size="small"
              inputProps={{ min: 1, step: 1 }}
              value={forFunVoteThreshold}
              onChange={(e) => setForFunVoteThreshold(e.target.value)}
              helperText="Every For fun match ends at this vote count, regardless of rank. Saving restamps in-progress For fun matches."
              sx={{ ...numberFieldSx, maxWidth: "480px", mt: 2 }}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} sx={{ order: 23 }}>
          <ScheduledChanges
            disabled={loading || saving}
            onSchedule={handleScheduleChange}
          />
        </Grid>

        {categoryThresholdsAvailable && (
          <Grid item xs={12} sx={{ order: 22 }}>
            <Paper sx={cardSx("#5D4037")}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Category Vote Thresholds
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Give a category its own &quot;first to X votes&quot; table
                instead of the Vote Thresholds above &mdash; for example make
                Fashion pagents need more votes to end. A pagent in an enabled
                category is stamped, displayed, and ended at its
                category&apos;s number for its rank; categories left off follow
                the default table. Saving applies changes to in-progress
                pagents of that category immediately, exactly like the default
                table: their &quot;First to X&quot; moves to the new number,
                and any already at or over a lowered target ends right away.
                Rap battles are live and end on the battle timer, so they have
                no threshold. Contest pagents are unaffected.
              </Typography>
              <Grid container spacing={2}>
                {VOTE_THRESHOLD_CATEGORIES.map(({ key, label }) => {
                  const override = categoryThresholdOverrides[key];
                  return (
                    <Grid item xs={12} sm={6} md={6} lg={4} key={key}>
                      <Box
                        sx={{
                          border: "1px solid rgba(255, 255, 255, 0.7)",
                          backgroundColor: "rgba(255, 255, 255, 0.72)",
                          borderRadius: "8px",
                          padding: "12px",
                          height: "100%",
                          boxSizing: "border-box",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={override.enabled}
                              onChange={(e) =>
                                toggleCategoryThresholdOverride(key, e.target.checked)
                              }
                            />
                          }
                          label={
                            <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
                          }
                        />
                        {override.enabled ? (
                          renderRankTable(
                            override.thresholds,
                            (rank, value) => setCategoryThreshold(key, rank, value),
                            "First to X",
                            1
                          )
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ pl: 1, pb: 0.5 }}
                          >
                            Uses the default vote thresholds.
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} sx={{ order: 10 }}>
          <Paper sx={cardSx("#1565C0")}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Default Feed Weights by Rank
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The voting feed orders matches by weight &times; random, so
              higher-weight ranks surface more often. The &quot;Filling
              bar&quot; column applies to voters still earning energy; the
              &quot;Full bar&quot; column applies once their energy bar is
              full, and always to guests. The &quot;Consolidated&quot; column applies
              to everyone instead of the other two while Feed Consolidation is
              on. All 1s means every rank appears equally. Weights are
              relative to each other; decimals are allowed. For fun matches have
              no public rank, so they use the For fun row instead of a tier.
              A category rank override does not replace that row. Category
              feed weights, the group-pagent boost, and the old-pagent rate
              still multiply on top of it.
            </Typography>
            {renderRankWeightColumns(
              [
                {
                  label: "Filling bar",
                  values: preEnergyRankWeights,
                  onChange: (rank, value) =>
                    setPreEnergyRankWeights((prev) => ({ ...prev, [rank]: value })),
                },
                {
                  label: "Full bar",
                  values: fullEnergyRankWeights,
                  onChange: (rank, value) =>
                    setFullEnergyRankWeights((prev) => ({ ...prev, [rank]: value })),
                },
                {
                  label: "Consolidated",
                  values: consolidatedRankWeights,
                  onChange: (rank, value) =>
                    setConsolidatedRankWeights((prev) => ({
                      ...prev,
                      [rank]: value,
                    })),
                },
              ],
              {
                label: "For fun",
                cells: [
                  {
                    key: "filling bar",
                    value: forFunFeedWeights.preEnergy,
                    onChange: (value) =>
                      setForFunFeedWeights((prev) => ({ ...prev, preEnergy: value })),
                  },
                  {
                    key: "full bar",
                    value: forFunFeedWeights.fullEnergy,
                    onChange: (value) =>
                      setForFunFeedWeights((prev) => ({ ...prev, fullEnergy: value })),
                  },
                  {
                    key: "consolidated",
                    value: forFunFeedWeights.consolidated,
                    onChange: (value) =>
                      setForFunFeedWeights((prev) => ({ ...prev, consolidated: value })),
                  },
                ],
              }
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} sx={{ order: 4 }}>
          <Paper sx={cardSx("#6D4C41")}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Category Preference Counts
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              How many users have each category selected in their category
              preferences — the selection the voting feed filters by (a
              user&apos;s in-app picks, or their signup picks if never
              changed). Users with nothing selected see every category and are
              counted once below the table rather than in every row. Every new
              preference save force-includes Rap. Read-only; refreshed when
              this page loads.
            </Typography>
            {categoryPreferences?.categories?.length ? (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx}>Category</TableCell>
                        <TableCell sx={headCellSx} align="right">
                          Users
                        </TableCell>
                        <TableCell sx={headCellSx} align="right">
                          % of users
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categoryPreferences.categories.map((row) => (
                        <TableRow key={row.categoryId}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {row.name || row.categoryShortName}
                          </TableCell>
                          <TableCell align="right">
                            {formatCount(row.users)}
                          </TableCell>
                          <TableCell align="right">
                            {formatShare(
                              row.users,
                              categoryPreferences.totalActiveUsers
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {formatCount(categoryPreferences.usersWithNoPreferences)} of{" "}
                  {formatCount(categoryPreferences.totalActiveUsers)} users have
                  no categories selected and see every category.
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No category preference counts available.
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} sx={{ order: 15 }}>
          <Paper sx={cardSx("#0277BD")}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Category Weight Overrides
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Give a category its own feed weight tables instead of the
              defaults above — filling bar, full bar, and (while Feed
              Consolidation is on) consolidated. Categories left off follow
              the default weights. To simply make a whole category more or
              less common, use Category Feed Weights above instead; the two
              stack. Rap and karaoke live battles use these override tables
              for their voting-feed interleave weight the same way regular
              matches use them in the pipeline; when off, they use the
              default Rank Weights.
            </Typography>
            <Grid container spacing={2}>
              {FEED_WEIGHT_CATEGORIES.map(({ key, label }) => {
                const override = categoryOverrides[key];
                return (
                  <Grid item xs={12} md={6} key={key}>
                    <Box
                      sx={{
                        border: "1px solid rgba(255, 255, 255, 0.7)",
                        backgroundColor: "rgba(255, 255, 255, 0.72)",
                        borderRadius: "8px",
                        padding: "12px",
                        height: "100%",
                        boxSizing: "border-box",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={override.enabled}
                            onChange={(e) =>
                              toggleCategoryOverride(key, e.target.checked)
                            }
                          />
                        }
                        label={
                          <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
                        }
                      />
                      {override.enabled ? (
                        renderRankWeightColumns([
                          {
                            label: "Filling bar",
                            values: override.preEnergy,
                            onChange: (rank, value) =>
                              setCategoryWeight(key, "preEnergy", rank, value),
                          },
                          {
                            label: "Full bar",
                            values: override.fullEnergy,
                            onChange: (rank, value) =>
                              setCategoryWeight(key, "fullEnergy", rank, value),
                          },
                          {
                            label: "Consolidated",
                            values: override.consolidated,
                            onChange: (rank, value) =>
                              setCategoryWeight(key, "consolidated", rank, value),
                          },
                        ])
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ pl: 1, pb: 0.5 }}
                        >
                          Uses the default Rank Weights.
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      <InProgressMatchesSection
        refreshToken={matchesRefreshToken}
        oldMatchFeedBoost={oldMatchFeedBoost}
      />

      <Divider sx={{ my: 4 }} />

      <SearchingPostsSection />
    </PageLayout>
  );
};

export default GameplaySettings;
